'use client'

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useApolloClient } from '@apollo/client/react'
import type { ApolloClient } from '@apollo/client'
import { nhost } from '@/lib/nhost'
import { reconnectWebSocket } from '@/lib/apollo-client'
import { fetchTenantsWithProperties, Tenant, Property } from '@/services/tenants'
import { updateUserMetadata } from '@/services/user'
import { getImpersonation, setImpersonation, subscribeImpersonation } from '@/lib/impersonation'
import { canUseTenantSeed } from '@/contexts/selection-seed'
import { setPropertyRoleKey } from '@/lib/active-role'
import { gqlRequest } from '@/services/graphql'
import { GetUserRoleForPropertyDocument } from '@/graphql/menu.generated'

/**
 * The active tenant/property live in the user's metadata. While impersonating, that identity is the
 * overlay's user (per-tab), not the admin's real session — read from whichever is active.
 */
function readSelectionMeta(): { tenantId: string | null; propertyId: string | null } {
  const impersonation = getImpersonation()
  const meta = (impersonation
    ? impersonation.user.metadata
    : nhost.getUserSession()?.user?.metadata) as Record<string, unknown> | undefined
  return {
    tenantId: ((meta?.['tenant-id'] as string) || null),
    propertyId: ((meta?.['active_property_id'] as string) || null),
  }
}

/**
 * Resolve the current user's property-level role_key for a property and
 * publish it to lib/active-role BEFORE subscriptions/queries restart, so the
 * automatic per-property x-hasura-role header (when enabled) is correct from
 * the first request after a switch. Failure or no membership row → null →
 * requests fall back to the token's default role.
 */
async function resolveAndPublishPropertyRole(propertyId: string | null): Promise<void> {
  try {
    const impersonation = getImpersonation()
    const userId = impersonation
      ? impersonation.user.id
      : nhost.getUserSession()?.user?.id ?? null
    if (!userId || !propertyId) {
      setPropertyRoleKey(null)
      return
    }
    const response = await gqlRequest(GetUserRoleForPropertyDocument, {
      user_id: userId,
      property_id: propertyId,
    })
    setPropertyRoleKey(response.body.data?.property_members?.[0]?.role_key ?? null)
  } catch {
    setPropertyRoleKey(null)
  }
}

/**
 * Reset Apollo's cache on a tenant/property switch without tripping the
 * QueryManager's "Store reset while query was in flight" invariant.
 *
 * client.clearStore() / resetStore() run through QueryManager.clearStore(),
 * which THROWS an InvariantError whenever any HTTP query is mid-flight. On a
 * tenant/property switch there's almost always one in flight (the user just
 * clicked a switcher that coexists with dashboard widgets). The thrown error
 * is informational — Apollo's telling us the outstanding query won't land in
 * the new cache — but Next.js's dev overlay surfaces any thrown Error as a
 * red-box regardless of our try/catch.
 *
 * Going one level lower — client.cache.reset() — avoids the QueryManager
 * altogether and just wipes the cache. We then ask each observed query to
 * refetch under the new (by now updated) variables. Identical end state,
 * zero dev-overlay noise.
 */
async function resetStoreQuietly(client: ApolloClient): Promise<void> {
  await client.cache.reset()
}

async function refetchActiveQuietly(client: ApolloClient): Promise<void> {
  // reFetchObservableQueries re-issues every watched query with its current
  // variables. Safe to call while queries are in flight — it just schedules
  // a new fetch and lets the old one complete into a void cache.
  await client.reFetchObservableQueries()
}

interface SelectionContextType {
  tenants: Tenant[]
  loading: boolean
  error: string | null
  selectedTenantId: string | null
  selectedPropertyId: string | null
  propertiesForSelectedTenant: Property[]
  setTenant: (tenantId: string | null) => Promise<void>
  setProperty: (propertyId: string | null) => Promise<void>
  selectionVersion: number
}

export const SelectionContext = createContext<SelectionContextType | undefined>(undefined)

export function SelectionProvider({
  children,
  initialTenantId = null,
  initialPropertyId = null,
  initialTenants = null,
}: {
  children: React.ReactNode
  // Seeded from JWT claims server-side (RSC layout) so property-scoped queries have a valid id on
  // the FIRST render — this is what closes the `unexpected null value for type 'uuid'` window.
  // Effects A/B below still reconcile from metadata and run the "tenant but no property" backfill.
  initialTenantId?: string | null
  initialPropertyId?: string | null
  /**
   * Server-fetched tenants+properties (lib/dashboard/header-server.ts) so the
   * tenant/property switchers show real names in the SSR HTML. The mount fetch
   * still runs (it also drives onboarding redirect + property auto-select) but
   * as a background refresh — no loading flash over the seeded names.
   */
  initialTenants?: Tenant[] | null
}) {
  const apolloClient = useApolloClient()
  const router = useRouter()
  const pathname = usePathname()
  const [tenants, setTenants] = useState<Tenant[]>(() => initialTenants ?? [])
  const [loading, setLoading] = useState<boolean>(!initialPropertyId && !initialTenants)
  const [error, setError] = useState<string | null>(null)
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(initialTenantId)
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(initialPropertyId)
  const [selectionVersion, setSelectionVersion] = useState<number>(0)

  // Read the active tenant/property (from the overlay while impersonating, else the real session)
  // and push into local state.
  const syncFromSession = useCallback(() => {
    const { tenantId, propertyId } = readSelectionMeta()
    setSelectedTenantId(tenantId)
    setSelectedPropertyId(propertyId)
    return { tenantId, propertyId }
  }, [])

  // Keep the selection in sync with session changes and impersonation start/stop.
  useEffect(() => {
    syncFromSession()
    const unsubscribe = nhost.sessionStorage.onChange(() => syncFromSession())
    const unsubImpersonation = subscribeImpersonation(() => syncFromSession())
    return () => {
      unsubscribe()
      unsubImpersonation()
    }
  }, [syncFromSession])

  // fetch tenants and auto-select property if needed
  useEffect(() => {
    let mounted = true

    async function loadTenants() {
      // The SSR seed (lib/dashboard/header-server.ts) is fetched under the user's
      // *cookie session*. While impersonating, that cookie is the ADMIN's real
      // session — the act-as overlay is a per-tab sessionStorage token the server
      // can't see — so the admin role bypasses RLS and the seed lists EVERY tenant
      // on the platform. Trusting it would (a) show tenants outside the acted-as
      // user's allowed set and (b) break switching (the switch route rejects any
      // tenant not in x-hasura-all-tenant-id → silent no-op). So under impersonation
      // we ignore the seed and fetch under the overlay token (RLS-scoped to the
      // acted-as user via the browser authLink), exactly as switching does.
      const useSeed = canUseTenantSeed(!!initialTenants, !!getImpersonation())

      // Seeded mounts skip the network entirely — the server fetched this exact
      // query (same RLS) during this request, so refetching is a redundant round
      // trip. The post-processing below (onboarding redirect + property
      // auto-select) runs against the seed data instead. Tenant switches (setTenant)
      // still fetch fresh — they run under a re-minted JWT whose RLS differs.
      if (!useSeed) setLoading(true)
      try {
        const data = useSeed ? initialTenants! : await fetchTenantsWithProperties()
        if (!mounted) return
        setTenants(data)

        // Redirect to onboarding if user has no tenants
        // Skip if already on onboarding or admin routes
        if (data.length === 0 && !pathname?.startsWith('/onboarding') && !pathname?.startsWith('/admin')) {
          router.push('/onboarding')
          return
        }

        // Auto-select first property if tenant is selected but property is not.
        // Skip while impersonating: the overlay has no refresh token, so we must not mutate the
        // acted-as user's metadata / try to re-mint a token.
        const { tenantId, propertyId } = syncFromSession()

        if (tenantId && !propertyId && !getImpersonation()) {
          const tenant = data.find(t => t.id === tenantId)
          const firstPropertyId = tenant?.properties?.[0]?.id ?? null
          if (firstPropertyId) {
            await updateUserMetadata({ 'tenant-id': tenantId, active_property_id: firstPropertyId })
            await resolveAndPublishPropertyRole(firstPropertyId)
            // Drop WS so the next subscription wave uses a token with the new active-property claim.
            reconnectWebSocket()
            setSelectedPropertyId(firstPropertyId)
            setSelectionVersion(v => v + 1)
          }
        }
      } catch (err) {
        if (!mounted) return
        setError(err instanceof Error ? err.message : 'Failed to load tenants')
      } finally {
        if (mounted) setLoading(false)
      }
    }

    loadTenants()

    return () => {
      mounted = false
    }
  }, [syncFromSession, router, pathname, initialTenants])

  const propertiesForSelectedTenant = useMemo(() => {
    const tenant = tenants.find(t => t.id === selectedTenantId)
    return tenant?.properties || []
  }, [tenants, selectedTenantId])

  // Catch-all for the per-property role header: the switch paths publish the
  // role inline (before their WS reconnect); this covers the initial mount,
  // where the selection comes straight from the session with no switch.
  useEffect(() => {
    resolveAndPublishPropertyRole(selectedPropertyId)
  }, [selectedPropertyId])

  // While impersonating, the overlay token is non-refreshable, so we can't switch tenant/property
  // via updateUserMetadata()+refreshSession(). Instead re-mint the overlay with the new active
  // selection (validated server-side against the acted-as user's allowed set) so the admin can move
  // across all of the user's tenants/properties, just like the user could.
  const remintImpersonation = useCallback(
    async (tenantId: string | null, propertyId: string | null): Promise<boolean> => {
      const overlay = getImpersonation()
      if (!overlay) return false
      const res = await fetch('/api/admin/impersonate/switch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${overlay.accessToken}` },
        body: JSON.stringify({ tenantId: tenantId || undefined, propertyId: propertyId || undefined }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to switch during impersonation')
      setImpersonation({
        ...overlay,
        accessToken: data.accessToken,
        expiresAt: data.expiresAt ?? overlay.expiresAt,
        user: {
          ...overlay.user,
          metadata: { ...(overlay.user.metadata || {}), 'tenant-id': tenantId, active_property_id: propertyId },
        },
      })
      return true
    },
    [],
  )

  const setTenant = async (tenantId: string | null) => {
    setLoading(true)
    setError(null)

    try {
      // Impersonation: re-mint the overlay (no metadata mutation / refreshSession — the overlay has
      // no refresh token). Two stages, mirroring the normal flow: the properties RLS is scoped to
      // the ACTIVE tenant, so a tenant's properties are invisible until it's the active tenant.
      if (getImpersonation()) {
        // Stage 1 — make the new tenant active (property null) so RLS exposes its properties.
        await remintImpersonation(tenantId, null)
        const data = await fetchTenantsWithProperties()
        setTenants(data)
        const firstPropertyId = data.find(t => t.id === tenantId)?.properties?.[0]?.id ?? null
        // Stage 2 — adopt the tenant's first property so property-scoped queries have a valid id.
        if (firstPropertyId) await remintImpersonation(tenantId, firstPropertyId)
        await resolveAndPublishPropertyRole(firstPropertyId)
        reconnectWebSocket()
        await resetStoreQuietly(apolloClient)
        setSelectedTenantId(tenantId)
        setSelectedPropertyId(firstPropertyId)
        await refetchActiveQuietly(apolloClient)
        setSelectionVersion(v => v + 1)
        return
      }

      // Stage 1 — persist new tenant, clear property. updateUserMetadata() also
      // calls nhost.refreshSession(0) so the in-memory access token now carries
      // the new tenant-id claim (and no active-property-id claim yet).
      await updateUserMetadata({ 'tenant-id': tenantId, active_property_id: null })

      // Refetch tenants under the new JWT context so RLS lets us see the target.
      const data = await fetchTenantsWithProperties()
      setTenants(data)

      const refreshedTenant = data.find(t => t.id === tenantId)
      const firstPropertyId = refreshedTenant?.properties?.[0]?.id ?? null

      // Stage 2 — if a property auto-selected, persist it and get ANOTHER refreshed
      // token that carries BOTH claims before we open any property-scoped subscriptions.
      if (firstPropertyId) {
        await updateUserMetadata({ 'tenant-id': tenantId, active_property_id: firstPropertyId })
      }

      // Per-property role header must reflect the NEW property before any
      // query/subscription restarts below.
      await resolveAndPublishPropertyRole(firstPropertyId)

      // Critical: drop the old WebSocket so Hasura evaluates the NEW token for all
      // subscriptions opened after the state change below. Without this, existing
      // subscriptions (and any that mount when skip flips) keep running against the
      // WS's original token — which still has the previous tenant/property claims —
      // and Hasura rejects them with "missing session variable".
      reconnectWebSocket()

      // Cache is scoped by tenant/property; purge and refetch under the new
      // context. resetStoreQuietly swallows Apollo's expected in-flight
      // invariant (both the throw and the console.error).
      await resetStoreQuietly(apolloClient)

      // Publish the new selection only AFTER the WS has been torn down. Components
      // that gate subscriptions on useJwtClaims() will hold off until the
      // reconnected WS is up and carrying the refreshed token.
      setSelectedTenantId(tenantId)
      setSelectedPropertyId(firstPropertyId)

      await refetchActiveQuietly(apolloClient)

      setSelectionVersion(v => v + 1)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to switch tenant'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const setProperty = async (propertyId: string | null) => {
    setLoading(true)
    setError(null)

    try {
      // Impersonation: re-mint the overlay with the new active property (see setTenant).
      if (getImpersonation()) {
        await remintImpersonation(selectedTenantId, propertyId)
        await resolveAndPublishPropertyRole(propertyId)
        reconnectWebSocket()
        await resetStoreQuietly(apolloClient)
        setSelectedPropertyId(propertyId)
        await refetchActiveQuietly(apolloClient)
        setSelectionVersion(v => v + 1)
        return
      }

      // Persist + refresh JWT so the next WS connection carries the new
      // active-property-id claim. updateUserMetadata calls refreshSession(0).
      await updateUserMetadata({ 'tenant-id': selectedTenantId, active_property_id: propertyId })

      // Per-property role header must reflect the NEW property before any
      // query/subscription restarts below.
      await resolveAndPublishPropertyRole(propertyId)

      // Drop the WS BEFORE state changes so any subscription that mounts on the
      // next render uses a connection established with the refreshed token.
      reconnectWebSocket()

      // clearStore via resetStoreQuietly — suppresses the expected
      // "query was in flight" invariant that Apollo fires when we reset
      // during an active switch. See helper above for why.
      await resetStoreQuietly(apolloClient)

      // Publish new property selection after the cache is cleared so re-rendered
      // components fetch cleanly under the new claim.
      setSelectedPropertyId(propertyId)

      await refetchActiveQuietly(apolloClient)

      setSelectionVersion(v => v + 1)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to switch property'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const value: SelectionContextType = {
    tenants,
    loading,
    error,
    selectedTenantId,
    selectedPropertyId,
    propertiesForSelectedTenant,
    setTenant,
    setProperty,
    selectionVersion,
  }

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>
}

export function useSelection() {
  const ctx = useContext(SelectionContext)
  if (!ctx) throw new Error('useSelection must be used within SelectionProvider')
  return ctx
}

/**
 * The selection, or `null` where no `SelectionProvider` is mounted.
 *
 * ⛔ `SelectionProvider` lives under the DASHBOARD layout only. The growth and
 * agency portals mount neither it nor a tenant/property picker — they have no
 * active property to select. A hook that is reached from BOTH portals (the
 * shared CRUD `DataTable` reaches `usePropertyRole` and `useCapabilitySet`)
 * must therefore read the context optionally: `useSelection()` throws there,
 * and a throw inside a render is a blank page behind the portal's error
 * boundary, not a degraded one (prod 2026-09-01: every `/growth` CRUD screen —
 * demo-requests, leads, prospects, proposals, supplier-applications — rendered
 * "Growth Error"). `useDataTableQuery` already reads it this way; these two
 * hooks were the gap.
 */
export function useOptionalSelection() {
  return useContext(SelectionContext)
}
